import { useCallback, useMemo, type CSSProperties, type ReactNode } from 'react';

import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable, defaultAnimateLayoutChanges, type AnimateLayoutChanges } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable, type Row } from '@tanstack/react-table';
import type { RowData } from '@tanstack/react-table';

declare module '@tanstack/react-table' {
  interface ColumnMeta<TData extends RowData, TValue> {
    className?: string;
    style?: CSSProperties;
  }
}

import type { BundleItem } from './bundle-item-row';
import { buildBundlePreviewEntries, type BundlePreviewEntry } from './bundle-preview-utils';
import type { BundleConfig, BundleTextColor } from './config-tab';
import { EditableText } from './editable-text';
import {
  resolveBundleColumns,
  type BundleIndexColumnId,
} from '../../../../../../backend/src/matter/subset/bundle/bundle-index-layout';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/new/components/shadcn/table';

interface SharedEditorProps {
  items: BundleItem[];
  config: BundleConfig;
}

interface BundleContentsEditorProps extends SharedEditorProps {
  onItemsChange: (items: BundleItem[]) => void;
  onConfigChange: (config: BundleConfig) => void;
}

const FONT_FAMILY_STACKS: Record<'times' | 'helvetica' | 'courier', string> = {
  times: "'Times New Roman', Times, serif",
  helvetica: "'Helvetica Neue', Helvetica, Arial, sans-serif",
  courier: "'Courier New', Courier, monospace",
};

const TEXT_COLOR_HEX: Record<BundleTextColor, string> = {
  black: '#000000',
  gray: '#6b7280',
  blue: '#2563eb',
  red: '#dc2626',
};

// Suppress dnd-kit's drop animation — let the parent's reordered state drive
// the layout, so dropped rows shift into place without first animating back.
const animateLayoutChanges: AnimateLayoutChanges = (args) =>
  defaultAnimateLayoutChanges({ ...args, wasDragging: true });

function EditorShell({ title, subtitle, children }: { title: string; subtitle?: string; children: ReactNode }) {
  return (
    <div className="flex max-h-full min-h-0 flex-col rounded-md border border-gray-200 bg-gray-50 p-3">
      <div className="mb-3 flex shrink-0 items-center justify-between text-xs font-semibold tracking-wide text-gray-500 uppercase">
        <span>{title}</span>
        {subtitle && <span className="text-gray-400">{subtitle}</span>}
      </div>
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">{children}</div>
    </div>
  );
}

const columnHelper = createColumnHelper<BundlePreviewEntry>();

function SortableContentsEntry({
  row,
  onRename,
  onSetDate,
  onRemove,
}: {
  row: Row<BundlePreviewEntry>;
  onRename: (entryId: string, name: string) => void;
  onSetDate: (entryId: string, dateText: string) => void;
  onRemove: (entryId: string) => void;
}) {
  const entry = row.original;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: entry.id,
    animateLayoutChanges,
  });

  return (
    <TableRow
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`group bg-white ${isDragging ? 'relative z-10 shadow-lg/4' : ''}`}
    >
      {row.getVisibleCells().map((cell) => (
        <TableCell
          key={cell.id}
          className={cell.column.columnDef.meta?.className}
          style={cell.column.columnDef.meta?.style}
        >
          {cell.column.id === 'drag' ? (
            <button
              type="button"
              className="flex cursor-grab touch-none items-center justify-center rounded-full p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700"
              {...attributes}
              {...listeners}
              aria-label="Reorder contents entry"
            >
              <Icon className="lucide--grip-vertical size-4" />
            </button>
          ) : cell.column.id === 'tab' ? (
            <span className="font-medium text-gray-900">{entry.row.tab}</span>
          ) : cell.column.id === 'document' ? (
            <div className="min-w-0 flex-1">
              <EditableText
                value={entry.row.document}
                placeholder={entry.item.originalName}
                ariaLabel="Rename contents entry"
                wrap
                onCommit={(next) => onRename(entry.id, next)}
                className="block max-w-full text-left leading-tight font-medium text-gray-900"
              />
              {entry.item.customName && (
                <div className="truncate pt-0.5 text-xs leading-4 text-gray-500">{entry.item.originalName}</div>
              )}
            </div>
          ) : cell.column.id === 'date' ? (
            <EditableText
              value={entry.row.date}
              placeholder="—"
              ariaLabel="Edit date"
              wrap
              onCommit={(next) => onSetDate(entry.id, next)}
              className="block max-w-full text-left text-gray-600"
            />
          ) : cell.column.id === 'pages' ? (
            <span>{entry.row.pages}</span>
          ) : cell.column.id === 'edit' ? (
            <div className="flex items-center justify-end opacity-0 transition group-hover:opacity-100 focus-within:opacity-100">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onRemove(entry.id)}
                className="size-7 rounded-full p-0 text-gray-500 hover:text-red-600"
                aria-label="Remove contents entry"
              >
                <Icon className="lucide--trash-2 size-3.5" />
              </Button>
            </div>
          ) : (
            flexRender(cell.column.columnDef.cell, cell.getContext())
          )}
        </TableCell>
      ))}
    </TableRow>
  );
}

export const BundleContentsEditor = ({ items, config, onItemsChange, onConfigChange }: BundleContentsEditorProps) => {
  const entries = useMemo(() => buildBundlePreviewEntries(items), [items]);
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleRename = useCallback(
    (entryId: string, name: string) => {
      const item = items.find((candidate) => candidate.id === entryId);
      if (!item) return;
      const trimmed = name.trim();
      const customName = trimmed === '' || trimmed === item.originalName ? null : trimmed;
      onItemsChange(items.map((candidate) => (candidate.id === entryId ? { ...candidate, customName } : candidate)));
    },
    [items, onItemsChange],
  );

  const handleSetDate = useCallback(
    (entryId: string, dateText: string) => {
      const trimmed = dateText.trim();
      onItemsChange(
        items.map((candidate) =>
          candidate.id === entryId ? { ...candidate, dateText: trimmed === '' ? null : trimmed } : candidate,
        ),
      );
    },
    [items, onItemsChange],
  );

  const handleRemove = useCallback(
    (entryId: string) => {
      onItemsChange(items.filter((item) => item.id !== entryId).map((item, index) => ({ ...item, order: index })));
    },
    [items, onItemsChange],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      onItemsChange(arrayMove(items, oldIndex, newIndex).map((item, index) => ({ ...item, order: index })));
    },
    [items, onItemsChange],
  );

  const cellBorder = 'border border-gray-300 px-3 py-2 align-middle';

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'drag',
        header: () => null,
        cell: () => null,
        meta: { className: 'w-8 px-1 py-2 align-middle' },
      }),
      ...resolveBundleColumns(config.columns).map((column) =>
        columnHelper.display({
          id: column.id satisfies BundleIndexColumnId,
          header: () => column.label,
          cell: () => null,
          meta: {
            className: `${cellBorder} text-gray-900`,
            style: { width: `${column.fractionWidth * 100}%` },
          },
        }),
      ),
      columnHelper.display({
        id: 'edit',
        header: () => null,
        cell: () => null,
        meta: { className: 'w-8 px-1 py-2 align-middle' },
      }),
    ],
    [config.columns],
  );

  const table = useReactTable({
    data: entries,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
  });

  const fontFamilyStack = FONT_FAMILY_STACKS[config.fontFamily] ?? FONT_FAMILY_STACKS.times;

  return (
    <EditorShell title="Contents" subtitle={`${entries.length} item${entries.length === 1 ? '' : 's'}`}>
      <div
        className="flex min-h-0 flex-1 flex-col gap-3"
        style={{ fontSize: `${config.fontSize}px`, fontFamily: fontFamilyStack }}
      >
        {entries.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-300 bg-white px-4 py-5 text-sm text-gray-400">
            Add items to see your contents here.
          </div>
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto pr-2">
            <div className="relative mx-auto flex aspect-[210/297] w-full max-w-[600px] min-w-[420px] flex-col rounded-sm border border-gray-300 bg-white p-5">
              <span
                aria-hidden
                style={{
                  position: 'absolute',
                  left: `${config.pageNumberPosition.x}%`,
                  top: `${config.pageNumberPosition.y}%`,
                  transform: `translate(-${config.pageNumberPosition.x}%, -${config.pageNumberPosition.y}%)`,
                  color: TEXT_COLOR_HEX[config.textColor],
                  fontFamily: fontFamilyStack,
                  fontSize: `${config.pageNumberSize}px`,
                  fontWeight: config.pageNumberBold ? 700 : 400,
                  fontStyle: config.pageNumberItalic ? 'italic' : 'normal',
                  textDecoration: config.pageNumberUnderline ? 'underline' : 'none',
                  padding: '12px',
                  whiteSpace: 'nowrap',
                  lineHeight: 1,
                  pointerEvents: 'none',
                }}
              >
                1
              </span>
              <div className="mx-auto flex w-full flex-1 flex-col gap-2">
                <div className="shrink-0 space-y-2 py-1 text-center">
                  <EditableText
                    value={config.subheading}
                    placeholder="Subheading"
                    ariaLabel="Bundle subheading"
                    wrap
                    onCommit={(next) => onConfigChange({ ...config, subheading: next })}
                    className="mx-auto block max-w-xl px-2 text-lg font-semibold tracking-wide text-gray-900 uppercase transition"
                  />
                  <div className="mx-auto w-sm border-t border-gray-200" />
                  <EditableText
                    value={config.name}
                    ariaLabel="Bundle title"
                    wrap
                    onCommit={(next) => onConfigChange({ ...config, name: next === '' ? config.name : next })}
                    className="mx-auto my-3 block min-h-9 max-w-xl px-2 text-3xl font-semibold text-gray-900 transition"
                  />
                </div>

                <div className="bg-white pb-6">
                  <div>
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                      <Table className="table-fixed border-collapse">
                        <TableHeader>
                          {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id} className="text-left font-semibold text-gray-900">
                              {headerGroup.headers.map((header) => (
                                <TableHead
                                  key={header.id}
                                  className={`${header.column.columnDef.meta?.className ?? ''}`}
                                  style={header.column.columnDef.meta?.style}
                                >
                                  {header.isPlaceholder
                                    ? null
                                    : flexRender(header.column.columnDef.header, header.getContext())}
                                </TableHead>
                              ))}
                            </TableRow>
                          ))}
                        </TableHeader>

                        <TableBody>
                          <SortableContext
                            items={table.getRowModel().rows.map((row) => row.original.id)}
                            strategy={verticalListSortingStrategy}
                          >
                            {table.getRowModel().rows.map((row) => (
                              <SortableContentsEntry
                                key={row.id}
                                row={row}
                                onRename={handleRename}
                                onSetDate={handleSetDate}
                                onRemove={handleRemove}
                              />
                            ))}
                          </SortableContext>
                        </TableBody>
                      </Table>
                    </DndContext>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </EditorShell>
  );
};
